/**
 * 遊技機の PUSH ボタン風の大型トリガーボタン。
 *
 * 押すたびに onPress を発火する（再生中の押し直し = 即リトリガ）。
 * メタリックなベースリング + 赤いドームに白抜き PUSH、押下で沈み、
 * 再生中はイエローの発光がパルスする。
 */

import React from "react";
import styled, { css, keyframes } from "styled-components";

import { RENDA_FONT, RENDA_PANEL } from "../palette";

const pulse = keyframes`
  0%, 100% {
    box-shadow:
      0 0 6px 2px rgba(255, 210, 31, 0.5),
      0 7px 0 #7c0e24,
      0 12px 16px rgba(0, 0, 0, 0.35),
      inset 0 2px 6px rgba(255, 255, 255, 0.5);
  }
  50% {
    box-shadow:
      0 0 26px 10px rgba(255, 210, 31, 0.85),
      0 7px 0 #7c0e24,
      0 12px 16px rgba(0, 0, 0, 0.35),
      inset 0 2px 6px rgba(255, 255, 255, 0.5);
  }
`;

const Dome = styled.span<{ $playing: boolean }>`
  position: absolute;
  inset: 12px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(
    circle at 35% 28%,
    #ff97a8 0%,
    #ff4b63 40%,
    #d31f3e 75%,
    #a80f2c 100%
  );
  box-shadow:
    0 7px 0 #7c0e24,
    0 12px 16px rgba(0, 0, 0, 0.35),
    inset 0 2px 6px rgba(255, 255, 255, 0.5);
  transition:
    transform 0.05s ease-out,
    box-shadow 0.05s ease-out;

  ${({ $playing }) =>
    $playing &&
    css`
      animation: ${pulse} 0.5s ease-in-out infinite;
    `}
`;

const DomeText = styled.span`
  font-family: ${RENDA_FONT};
  font-weight: 800;
  font-size: 22px;
  letter-spacing: 0.16em;
  /* 中央寄せの見た目を letter-spacing ぶん補正 */
  margin-right: -0.16em;
  color: #ffffff;
  text-shadow: 0 2px 3px rgba(0, 0, 0, 0.45);
  user-select: none;
`;

const Root = styled.button`
  position: relative;
  width: 132px;
  height: 132px;
  padding: 0;
  border-radius: 50%;
  border: 3px solid ${RENDA_PANEL.outline};
  cursor: pointer;
  background: conic-gradient(
    from 45deg,
    #d6dde8,
    #97a1b2,
    #eef2f8,
    #9aa5b5,
    #d6dde8
  );
  box-shadow:
    0 6px 14px rgba(37, 42, 64, 0.35),
    inset 0 2px 3px rgba(255, 255, 255, 0.7);

  &:active:not(:disabled) ${Dome} {
    transform: translateY(6px);
    box-shadow:
      0 1px 0 #7c0e24,
      0 3px 6px rgba(0, 0, 0, 0.35),
      inset 0 2px 6px rgba(255, 255, 255, 0.5);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 3px solid ${RENDA_PANEL.focus};
    outline-offset: 3px;
  }
`;

export interface PushButtonProps {
  isPlaying: boolean;
  disabled?: boolean;
  onPress: () => void;
}

const PushButton: React.FC<PushButtonProps> = ({
  isPlaying,
  disabled = false,
  onPress,
}) => (
  <Root
    type="button"
    disabled={disabled}
    onClick={onPress}
    aria-label="連打を再生（押すたびにリトリガ）"
  >
    <Dome $playing={isPlaying}>
      <DomeText>PUSH</DomeText>
    </Dome>
  </Root>
);

export default PushButton;
